import { useState } from 'react';
import { 
  Globe, ExternalLink, Edit2, Trash2, AlertCircle, Check, X, MoreHorizontal
} from 'lucide-react';
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { domainsApi } from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const domainStatusColors = {
  'Live': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Redirected': 'bg-blue-50 text-blue-700 border-blue-200',
  'Parked': 'bg-amber-50 text-amber-700 border-amber-200',
  'Cancelled': 'bg-slate-100 text-slate-600 border-slate-200',
};

export const DomainTable = ({ domains, options, clientId, onEdit, onRefresh }) => {
  const [deletingDomain, setDeletingDomain] = useState(null);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const isRenewalSoon = (dateStr, renewalOwner) => {
    if (!dateStr || renewalOwner !== 'Boxit In-house') return false;
    const renewalDate = new Date(dateStr);
    const today = new Date();
    const diffTime = renewalDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 30;
  };

  const handleDelete = async () => {
    if (!deletingDomain) return;
    try {
      await domainsApi.delete(deletingDomain.id);
      toast.success('Domain deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete domain');
    } finally {
      setDeletingDomain(null);
    }
  };

  if (domains.length === 0) {
    return (
      <div className="bg-slate-50 rounded-lg p-8 text-center">
        <Globe className="w-10 h-10 text-slate-300 mx-auto mb-3" strokeWidth={1} />
        <p className="text-sm text-slate-500">No domains added yet.</p>
      </div>
    );
  }

  return (
    <>
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <Table data-testid="domains-table">
          <TableHeader>
            <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
              <TableHead className="h-10 px-3 text-xs font-semibold text-slate-600 uppercase">Domain</TableHead>
              <TableHead className="h-10 px-3 text-xs font-semibold text-slate-600 uppercase">Renewal Owner</TableHead>
              <TableHead className="h-10 px-3 text-xs font-semibold text-slate-600 uppercase">Renewal Date</TableHead>
              <TableHead className="h-10 px-3 text-xs font-semibold text-slate-600 uppercase">Auto-Renew</TableHead>
              <TableHead className="h-10 px-3 text-xs font-semibold text-slate-600 uppercase">Registrar</TableHead>
              <TableHead className="h-10 px-3 text-xs font-semibold text-slate-600 uppercase">Status</TableHead>
              <TableHead className="h-10 px-3 text-xs font-semibold text-slate-600 uppercase w-[60px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {domains.map((domain) => (
              <TableRow 
                key={domain.id} 
                className={cn(
                  "group",
                  isRenewalSoon(domain.renewal_date, domain.renewal_owner) && "bg-amber-50/50"
                )}
                data-testid={`domain-row-${domain.id}`}
              >
                <TableCell className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
                    <span className="font-medium text-slate-900 text-sm">{domain.domain_name}</span>
                    {domain.website_url && (
                      <a 
                        href={domain.website_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-slate-400 hover:text-indigo-600"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                </TableCell>
                <TableCell className="px-3 py-2">
                  <Badge 
                    variant="outline" 
                    className={cn(
                      "text-xs",
                      domain.renewal_owner === 'Boxit In-house' 
                        ? 'bg-indigo-50 text-indigo-700 border-indigo-200' 
                        : 'bg-slate-50 text-slate-600 border-slate-200'
                    )}
                  >
                    {domain.renewal_owner}
                  </Badge>
                </TableCell>
                <TableCell className="px-3 py-2">
                  <span className={cn(
                    "text-sm",
                    isRenewalSoon(domain.renewal_date, domain.renewal_owner) 
                      ? "text-amber-600 font-medium flex items-center gap-1" 
                      : "text-slate-600"
                  )}>
                    {isRenewalSoon(domain.renewal_date, domain.renewal_owner) && (
                      <AlertCircle className="w-3.5 h-3.5" />
                    )}
                    {formatDate(domain.renewal_date)}
                  </span>
                </TableCell>
                <TableCell className="px-3 py-2">
                  {domain.auto_renew ? (
                    <Check className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <X className="w-4 h-4 text-slate-400" />
                  )}
                </TableCell>
                <TableCell className="px-3 py-2 text-sm text-slate-600">
                  {domain.registrar}
                </TableCell>
                <TableCell className="px-3 py-2">
                  <Badge 
                    variant="outline" 
                    className={cn("text-xs", domainStatusColors[domain.status])}
                  >
                    {domain.status}
                  </Badge>
                </TableCell>
                <TableCell className="px-3 py-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7 opacity-0 group-hover:opacity-100"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onEdit(domain)}>
                        <Edit2 className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => setDeletingDomain(domain)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingDomain} onOpenChange={() => setDeletingDomain(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Domain</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deletingDomain?.domain_name}</strong>? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
